import { useState, useRef, useEffect, Dispatch, SetStateAction } from "react";
import { toast } from "sonner";
import { useUser } from "./useUser";
import { useDocument } from "./useDocument";

const apiUrl = process.env.API_BASE_URL || "http://localhost:8000";

interface UseDictationOptions {
  onTranscript?: Dispatch<SetStateAction<string>>;
  dictationUrl?: string;
  usePCMFormat?: boolean;
  socketParams?: {
    user?: string;
    document?: string | string[];
    current_document?: string;
    [key: string]: string | number | boolean | object | undefined;
  };
}

export const useDictation = (options: UseDictationOptions = {}) => {
  const { onTranscript, usePCMFormat = true } = options;
  const [isRecording, setIsRecording] = useState(false);
  const [isMicroLoading, setIsMicroLoading] = useState(false);
  const [isConverting, setIsConverting] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [isSpeaking, setIsSpeaking] = useState(false);
  const socketRef = useRef<WebSocket | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioStreamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const audioProcessorRef = useRef<ScriptProcessorNode | null>(null);
  const silenceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const checkRecorderDataRef = useRef<NodeJS.Timeout | null>(null);

  // Refs to track state inside closures/loops
  const isRecordingRef = useRef(isRecording);
  const isMicroLoadingRef = useRef(isMicroLoading);

  const isSpeakingRef = useRef(isSpeaking);

  useEffect(() => {
    isSpeakingRef.current = isSpeaking;
  }, [isSpeaking]);

  useEffect(() => {
    isRecordingRef.current = isRecording;
  }, [isRecording]);

  useEffect(() => {
    isMicroLoadingRef.current = isMicroLoading;
  }, [isMicroLoading]);

  const { user } = useUser();
  const { data: doc } = useDocument();

  const cleanupAudio = () => {
    if (socketRef.current) {
      socketRef.current.close();
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
      mediaRecorderRef.current.stop();
    }
    if (audioProcessorRef.current) {
      audioProcessorRef.current.disconnect();
      audioProcessorRef.current = null;
    }
    audioStreamRef.current?.getTracks().forEach((track) => track.stop());
    if (audioContextRef.current && audioContextRef.current.state !== "closed") {
      audioContextRef.current.close();
    }
    if (checkRecorderDataRef.current) {
      clearInterval(checkRecorderDataRef.current);
      checkRecorderDataRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      cleanupAudio();
    };
  }, []);

  const startRecording = async () => {
    if (isRecordingRef.current || isMicroLoadingRef.current) return;

    setIsMicroLoading(true);
    isMicroLoadingRef.current = true;
    console.log("[DictationDebug] startRecording: Setting isMicroLoading to true");

    // Check for secure context - getUserMedia requires HTTPS
    if (!window.isSecureContext) {
      console.error(
        "[DictationDebug] Microphone access requires a secure context (HTTPS). Current origin is not secure."
      );
      toast.error("Microphone Access Denied", {
        description:
          "Microphone requires a secure connection (HTTPS). Please ensure the add-in is served over HTTPS.",
      });
      setIsMicroLoading(false);
      isMicroLoadingRef.current = false;
      return;
    }

    try {
      // Read token synchronously to avoid breaking the user-gesture chain
      // required by getUserMedia (an async gap before it causes silent permission denial)
      const token = localStorage.getItem("accessToken")?.trim();
      if (!token) {
        toast.error("Authentication required", {
          description: "Please log in to use voice features.",
        });
        setIsMicroLoading(false);
        isMicroLoadingRef.current = false;
        return;
      }

      // On Word Online the add-in runs in a sandboxed iframe; getUserMedia is
      // blocked unless we first request device permission through the Office API.
      if (typeof Office !== "undefined" && Office.context?.platform === Office.PlatformType.OfficeOnline && Office.devicePermission?.requestPermissions) {
        await Office.devicePermission.requestPermissions([Office.DevicePermissionType.microphone]);
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStreamRef.current = stream;

      // Auth token is sent as a query param per backend requirement. ws: is only
      // used for local development; production always runs over HTTPS/wss:.
      const backendApiUrl = new URL(apiUrl);
      const wsProtocol = backendApiUrl.protocol === "https:" ? "wss:" : "ws:";
      const wsPath = options.dictationUrl || "/voice2text/livestreaming";

      const wsUrl = new URL(wsPath, `${wsProtocol}//${backendApiUrl.host}`);
      wsUrl.searchParams.set("token", token);

      socketRef.current = new WebSocket(wsUrl.toString());

      // Common WebSocket handlers
      socketRef.current.onopen = () => {
        console.log("[DictationDebug] WebSocket connected");
        const defaultParams = {
          user: user?.id,
          document: doc?._id ? [doc._id] : [],
          current_document: doc?._id,
        };
        const socketParams = options.socketParams
          ? { ...defaultParams, ...options.socketParams }
          : defaultParams;
        console.log("[DictationDebug] WebSocket onopen: Sending initial parameters:", socketParams);
        socketRef.current?.send(JSON.stringify(socketParams));

        if (!usePCMFormat && mediaRecorderRef.current) {
          mediaRecorderRef.current.start(1000);
        } else {
          // For PCM, we are already "recording" via the script processor, but we update state here
          setIsMicroLoading(false);
          isMicroLoadingRef.current = false;
          setIsRecording(true);
          isRecordingRef.current = true;
        }
      };

      socketRef.current.onmessage = (event) => {
        const newTranscript = event.data;
        console.log("[DictationDebug] Received text:", newTranscript);

        // Try to parse as JSON first (logic from web app)
        try {
          const data = JSON.parse(newTranscript);
          if (data.text) {
            setTranscript((prev) => prev + data.text.trim());
            if (onTranscript) onTranscript((prev) => prev + data.text.trim());
          } else if (typeof data === "string") {
            // Fallback if it's just a string in JSON
            setTranscript((prev) => prev + data);
            if (onTranscript) onTranscript((prev) => prev + data);
          }
        } catch (e) {
          // Plain text
          if (newTranscript) {
            setTranscript((prev) => prev + newTranscript);
            if (onTranscript) {
              console.log("[DictationDebug] Calling onTranscript with new text");
              onTranscript((prev) => prev + newTranscript);
            } else {
              console.warn("[DictationDebug] onTranscript callback is missing!");
            }
          }
        }
      };

      socketRef.current.onclose = () => {
        console.log("[DictationDebug] WebSocket disconnected");
        setIsRecording(false);
        isRecordingRef.current = false;
        setIsMicroLoading(false);
        isMicroLoadingRef.current = false;
        setIsConverting(false);
        setIsSpeaking(false);
        cleanupAudio();
      };

      socketRef.current.onerror = (error) => {
        console.error("[DictationDebug] WebSocket error:", error);
        setIsRecording(false);
        isRecordingRef.current = false;
        setIsMicroLoading(false);
        isMicroLoadingRef.current = false;
        setIsConverting(false);
        setIsSpeaking(false);
        cleanupAudio();
      };

      if (usePCMFormat) {
        console.log("[DictationDebug] Starting PCM recording...");
        // Setup Web Audio API for PCM conversion
        const audioContext = new AudioContext();
        audioContextRef.current = audioContext;
        const source = audioContext.createMediaStreamSource(stream);
        // Use 4096 buffer size for better resampling
        const processor = audioContext.createScriptProcessor(4096, 1, 1);
        audioProcessorRef.current = processor;

        source.connect(processor);
        processor.connect(audioContext.destination);

        const targetSampleRate = 16000;
        const sourceSampleRate = audioContext.sampleRate;
        console.log(
          `[DictationDebug] Audio Sample Rate: Source ${sourceSampleRate}, Target ${targetSampleRate}`
        );

        processor.onaudioprocess = (e: AudioProcessingEvent) => {
          if (socketRef.current?.readyState !== WebSocket.OPEN) {
            return;
          }

          const inputData = e.inputBuffer.getChannelData(0);

          // Simple speech detection on PCM data
          let sum = 0;
          const sampleStep = 100;
          for (let i = 0; i < inputData.length; i += sampleStep) {
            // Sample every 100th point for efficiency
            sum += Math.abs(inputData[i] || 0);
          }
          const avg = sum / (inputData.length / sampleStep);

          if (avg > 0.01) {
            // Threshold
            if (!isSpeakingRef.current) setIsSpeaking(true);
            if (silenceTimerRef.current) {
              clearTimeout(silenceTimerRef.current);
              silenceTimerRef.current = null;
            }
          } else {
            if (isSpeakingRef.current && !silenceTimerRef.current) {
              silenceTimerRef.current = setTimeout(() => {
                setIsSpeaking(false);
                silenceTimerRef.current = null;
              }, 500);
            }
          }

          // Resample from source rate to 16kHz if needed
          let resampledData: Float32Array;
          if (sourceSampleRate !== targetSampleRate) {
            const sampleRateRatio = sourceSampleRate / targetSampleRate;
            const newLength = Math.round(inputData.length / sampleRateRatio);
            resampledData = new Float32Array(newLength);

            for (let i = 0; i < newLength; i++) {
              const srcIndex = i * sampleRateRatio;
              const srcIndexInt = Math.floor(srcIndex);
              const srcIndexFrac = srcIndex - srcIndexInt;

              // Linear interpolation
              if (srcIndexInt + 1 < inputData.length) {
                const val1 = inputData[srcIndexInt] || 0;
                const val2 = inputData[srcIndexInt + 1] || 0;
                resampledData[i] = val1 * (1 - srcIndexFrac) + val2 * srcIndexFrac;
              } else {
                resampledData[i] = inputData[srcIndexInt] || 0;
              }
            }
          } else {
            resampledData = inputData;
          }

          // Convert float32 [-1, 1] to int16 [-32768, 32767]
          const pcmData = new Int16Array(resampledData.length);
          for (let i = 0; i < resampledData.length; i++) {
            const val = resampledData[i] || 0;
            const s = Math.max(-1, Math.min(1, val));
            pcmData[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
          }

          // Send raw PCM binary data (pcm_s16le format at 16kHz)
          // console.log(`[DictationDebug] Sending PCM chunk: ${pcmData.byteLength} bytes`);
          socketRef.current.send(pcmData.buffer);
        };
      } else {
        // Fallback to MediaRecorder (original logic)
        console.log("[DictationDebug] Starting MediaRecorder recording...");

        // Audio Analysis for Speech Detection (Original logic)
        const audioContext = new AudioContext();
        audioContextRef.current = audioContext;
        const analyser = audioContext.createAnalyser();
        analyserRef.current = analyser;
        const source = audioContext.createMediaStreamSource(stream);
        source.connect(analyser);
        analyser.fftSize = 256;
        const bufferLength = analyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);

        const checkAudioLevel = () => {
          if (!isRecordingRef.current && !isMicroLoadingRef.current) return;
          analyser.getByteFrequencyData(dataArray);
          let sum = 0;
          for (let i = 0; i < bufferLength; i++) sum += dataArray[i] || 0;
          const average = sum / bufferLength;
          if (average > 10) {
            setIsSpeaking(true);
            if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
          } else {
            if (!silenceTimerRef.current) {
              silenceTimerRef.current = setTimeout(() => setIsSpeaking(false), 500);
            }
          }
          requestAnimationFrame(checkAudioLevel);
        };
        requestAnimationFrame(checkAudioLevel);

        const mimeType = "audio/webm;codecs=opus";
        const recorderOptions = MediaRecorder.isTypeSupported(mimeType)
          ? { mimeType }
          : { mimeType: "audio/webm" };
        mediaRecorderRef.current = new MediaRecorder(stream, recorderOptions);

        checkRecorderDataRef.current = setInterval(() => {
          if (
            mediaRecorderRef.current?.state === "recording" &&
            isMicroLoadingRef.current &&
            !isRecordingRef.current
          ) {
            setIsMicroLoading(false);
            isMicroLoadingRef.current = false;
            setIsRecording(true);
            isRecordingRef.current = true;
          }
        }, 500);

        mediaRecorderRef.current.ondataavailable = (event) => {
          if (event.data.size > 0 && socketRef.current?.readyState === WebSocket.OPEN) {
            socketRef.current.send(event.data);
          }
        };

        mediaRecorderRef.current.onstop = () => {
          if (isRecordingRef.current) setIsConverting(true);
          cleanupAudio();
          setIsRecording(false);
          isRecordingRef.current = false;
          setIsConverting(false);
        };
      }
    } catch (error) {
      console.error("[DictationDebug] Error starting recording:", error);
      cleanupAudio();
      setIsRecording(false);
      isRecordingRef.current = false;
      setIsMicroLoading(false);
      isMicroLoadingRef.current = false;
      setIsConverting(false);
      setIsSpeaking(false);
    }
  };

  const stopRecording = () => {
    console.log("[DictationDebug] stopRecording called");
    if (usePCMFormat) {
      if (isRecordingRef.current) setIsConverting(true);
      cleanupAudio();
      setIsRecording(false);
      isRecordingRef.current = false;
      setIsConverting(false);
      setIsSpeaking(false);
    } else {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
        mediaRecorderRef.current.stop();
      }
    }
  };

  const startDictation = () => {
    if (!isRecording && !isMicroLoading) {
      startRecording();
    } else {
      stopRecording();
    }
  };

  return {
    isRecording,
    isMicroLoading,
    isConverting,
    transcript,
    isSpeaking,
    startDictation,
    stopRecording,
    setTranscript,
  };
};
